import { Link } from "react-router-dom";

const PRIMARY_GRADIENT =
  "linear-gradient(135deg, #367d10 0%, #307609 48%, #286906 100%)";
const WALL_GRADIENT =
  "linear-gradient(135deg, #245f07 0%, #307609 48%, #1f8b3a 100%)";
const SECONDARY_GRADIENT =
  "linear-gradient(180deg, #ffffff 0%, #f7fbf3 100%)";
const SECONDARY_WARM_GRADIENT =
  "linear-gradient(180deg, rgba(255,255,255,0.76) 0%, rgba(247,251,243,0.7) 100%)";

const baseButtonClass =
  "inline-flex min-w-0 items-center justify-center gap-2 rounded-[999px] border text-center font-sans font-bold leading-tight no-underline transition-all duration-200 hover:brightness-110 active:scale-95 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80";

const sizeClass = {
  sm: "min-h-[44px] px-4 py-2 text-xs sm:text-sm",
  md: "min-h-[52px] px-6 py-3 text-sm",
  lg: "min-h-[60px] px-7 py-4 text-base",
  icon: "h-10 w-10 rounded-full p-0",
};

const variantClass = {
  primary:
    "border-transparent text-white shadow-[0_18px_34px_rgba(48,118,9,0.24),0_4px_14px_rgba(230,204,85,0.2)]",
  wall:
    "border-transparent text-white shadow-[0_14px_28px_rgba(48,118,9,0.2)]",
  secondary:
    "border-transparent text-[#307609] shadow-[0_10px_22px_rgba(0,0,0,0.1)] backdrop-blur-md",
  secondaryWarm:
    "border-transparent text-[#245f07] shadow-[0_10px_24px_rgba(78,67,24,0.1),inset_0_1px_0_rgba(255,255,255,0.78)] backdrop-blur-md hover:shadow-[0_14px_28px_rgba(78,67,24,0.14),inset_0_1px_0_rgba(255,255,255,0.9)]",
  darkSecondary:
    "border-white/12 bg-white/[0.06] text-white/78 shadow-[0_12px_30px_rgba(0,0,0,0.2)] backdrop-blur-xl hover:bg-white/[0.1] hover:text-white",
  ghost:
    "border-transparent bg-transparent text-[#307609] shadow-none hover:bg-[#307609]/8 hover:brightness-100",
  ghostLight:
    "border-transparent bg-transparent text-[#e9f6df] shadow-none hover:text-[#f4d94e] hover:brightness-100",
  text:
    "border-transparent bg-transparent px-0 py-0 text-gray-500 underline decoration-gray-300 underline-offset-4 shadow-none hover:text-[#307609] hover:brightness-100",
};

const variantStyle = {
  primary: { background: PRIMARY_GRADIENT },
  wall: { background: WALL_GRADIENT },
  secondary: { background: SECONDARY_GRADIENT },
  secondaryWarm: { background: SECONDARY_WARM_GRADIENT },
};

function mergeClassNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function IconSvg({ name, className = "h-5 w-5 shrink-0" }) {
  const commonProps = {
    "aria-hidden": "true",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
  };

  if (name === "arrowLeft") {
    return (
      <svg {...commonProps} viewBox="0 0 20 20">
        <path d="M12 4 6 10l6 6" />
        <path d="M7 10h9" />
      </svg>
    );
  }

  if (name === "arrowRight") {
    return (
      <svg {...commonProps} viewBox="0 0 20 20">
        <path d="m8 4 6 6-6 6" />
        <path d="M13 10H4" />
      </svg>
    );
  }

  if (name === "cards") {
    return (
      <svg {...commonProps}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 8.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v8.25A2.25 2.25 0 0 0 6 16.5h2.25m8.25-8.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-7.5A2.25 2.25 0 0 1 8.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 0 0-2.25 2.25v6" />
      </svg>

    );
  }

  if (name === "check") {
    return (
      <svg {...commonProps}>
        <path d="m20 6-11 11-5-5" />
      </svg>
    );
  }

  if (name === "copy") {
    return (
      <svg {...commonProps}>
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
    );
  }

  if (name === "edit") {
    return (
      <svg {...commonProps}>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    );
  }

  if (name === "heart") {
    return (
      <svg {...commonProps}>
        <path d="M20.8 8.8c0 5.5-8.8 10.3-8.8 10.3S3.2 14.3 3.2 8.8A4.8 4.8 0 0 1 12 6.2a4.8 4.8 0 0 1 8.8 2.6Z" />
      </svg>
    );
  }

  if (name === "mail") {
    return (
      <svg {...commonProps}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
      </svg>
    );
  }

  if (name === "order") {
    return (
      <svg {...commonProps}>
        <path d="m16 16 2 2 4-4" />
        <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 1.44.24" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    );
  }

  if (name === "plus") {
    return (
      <svg {...commonProps}>
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </svg>
    );
  }

  if (name === "share") {
    return (
      <svg {...commonProps}>
        <path d="M21 3 10 14" />
        <path d="m21 3-7 18-4-7-7-4 18-7Z" />
      </svg>
    );
  }

  if (name === "sprout") {
    return (
      <svg {...commonProps}>
        <path d="M12 20V11" />
        <path d="M12 12c0-3.6 2.7-6.3 7-6.6-.2 4.2-2.8 6.8-6.6 7" />
        <path d="M12 12c0-3.2-2.4-5.6-6.2-5.8.2 3.8 2.6 6.1 5.8 6.2" />
        <path d="M9 20h6" />
      </svg>
    );
  }

  if (name === "sparkles") {
    return (
      <svg {...commonProps}>
        <path d="M12 3 9.8 8.8 4 11l5.8 2.2L12 19l2.2-5.8L20 11l-5.8-2.2Z" />
        <path d="M19 3v4" />
        <path d="M21 5h-4" />
        <path d="M5 17v3" />
        <path d="M6.5 18.5h-3" />
      </svg>
    );
  }

  if (name === "wall") {
    return (
      <svg {...commonProps}>
        <rect x="3" y="4" width="7" height="7" rx="1.5" />
        <rect x="14" y="4" width="7" height="7" rx="1.5" />
        <rect x="3" y="15" width="7" height="7" rx="1.5" />
        <rect x="14" y="15" width="7" height="7" rx="1.5" />
      </svg>
    );
  }

  if (name === "users") {
    return (
      <svg {...commonProps}>
        <path d="M15 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }

  if (name === "x") {
    return (
      <svg {...commonProps}>
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>
    );
  }

  return null;
}

export function ActionButton({
  as,
  href,
  to,
  children,
  className = "",
  disabled = false,
  fullWidth = false,
  icon,
  iconClassName,
  iconPosition = "left",
  isLarge = false,
  size = "md",
  style,
  type = "button",
  variant = "primary",
  onClick,
  ...props
}) {
  const Component = as || (to ? Link : href ? "a" : "button");
  const actualSize = isLarge ? "lg" : size;
  const isNativeButton = Component === "button";
  const isDisabledLink = disabled && !isNativeButton;
  const classes = mergeClassNames(
    baseButtonClass,
    sizeClass[actualSize] || sizeClass.md,
    variantClass[variant] || variantClass.primary,
    fullWidth ? "w-full" : "",
    className
  );
  const buttonStyle = {
    ...(variantStyle[variant] || {}),
    ...(style || {}),
  };
  const Icon = icon ? <IconSvg name={icon} className={iconClassName} /> : null;
  const handleClick = (event) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };
  const componentProps = {
    className: classes,
    style: buttonStyle,
    onClick: handleClick,
    ...props,
  };

  if (isNativeButton) {
    componentProps.type = type;
    componentProps.disabled = disabled;
  } else {
    componentProps["aria-disabled"] = disabled || undefined;
    componentProps.tabIndex = isDisabledLink ? -1 : props.tabIndex;

    if (to) {
      componentProps.to = disabled ? "#" : to;
    } else if (href) {
      componentProps.href = disabled ? undefined : href;
    }
  }

  return (
    <Component {...componentProps}>
      {Icon && iconPosition !== "right" ? Icon : null}
      {children ? <span className="min-w-0">{children}</span> : null}
      {Icon && iconPosition === "right" ? Icon : null}
    </Component>
  );
}

export function PrimaryButton(props) {
  return <ActionButton {...props} variant={props.variant || "primary"} />;
}

export function SecondaryButton(props) {
  return <ActionButton {...props} variant={props.variant || "secondary"} />;
}
